/**
 * Post-login bootstrap fetches (ARCHITECTURE.md R23).
 *
 * Login warms the dyno via GET /status + 120s timeout; manifest, /users/me, and
 * project selector must use the same guardrails or they abort at the global 60s cap
 * while the tenant DB pool is still waking.
 */

import { api } from '../api';

export const BOOTSTRAP_TIMEOUT_MS = 120_000;
/** @deprecated Use BOOTSTRAP_TIMEOUT_MS — kept for AuthContext / DemoMagicSignIn exports */
export const LOGIN_TIMEOUT_MS = BOOTSTRAP_TIMEOUT_MS;
// /token uses the full bootstrap budget (R23). A shorter cap (e.g. 45s) falsely fails when the
// API is warm but busy — e.g. map fullscreen on a TV plus a second login on another device.
export const LOGIN_TOKEN_TIMEOUT_MS = BOOTSTRAP_TIMEOUT_MS;

const WARMUP_PATH = '/status';
/** Shared across tabs on the same origin so a second login window skips redundant warmup. */
const WARMUP_STORAGE_KEY = 'archon_login_warmed_at';
const WARMUP_FRESHNESS_MS = 5 * 60 * 1000;
// Warmup is a best-effort probe: never let a hung/unreachable backend block login here.
// If /status does not answer quickly we proceed straight to /token (which has its own timeout).
const WARMUP_TIMEOUT_MS = 8_000;
const BOOTSTRAP_RETRY_BACKOFFS_MS = [1200, 2500];
/** @deprecated Use BOOTSTRAP_RETRY_BACKOFFS_MS */
export const LOGIN_RETRY_BACKOFFS_MS = BOOTSTRAP_RETRY_BACKOFFS_MS;
export { BOOTSTRAP_RETRY_BACKOFFS_MS };

export function isRetryableBootstrapError(err) {
  if (!err) return false;
  const status = err.response?.status;
  if (status && status >= 400 && status < 500) return false;
  return (
    !status ||
    status >= 500 ||
    err.code === 'ECONNABORTED' ||
    err.code === 'ERR_NETWORK' ||
    (typeof err.message === 'string' && /timeout|network/i.test(err.message))
  );
}

/** Cheap public probe — boots Render dyno + DB pool before tenant-heavy GETs. */
export async function warmupBackend() {
  try {
    const last = Number(localStorage.getItem(WARMUP_STORAGE_KEY) || 0);
    if (last && Date.now() - last < WARMUP_FRESHNESS_MS) return true;
  } catch (_) {
    // localStorage unavailable
  }
  try {
    await api.get(WARMUP_PATH, { timeout: WARMUP_TIMEOUT_MS });
    try {
      localStorage.setItem(WARMUP_STORAGE_KEY, String(Date.now()));
    } catch (_) {}
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * GET with warmup + extended timeout + retry for cold-start tenant routes.
 * @param {string} url
 * @param {import('axios').AxiosRequestConfig} [config]
 */
export async function bootstrapGet(url, config = {}) {
  await warmupBackend();
  let lastError = null;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (attempt > 0) {
      const backoff = BOOTSTRAP_RETRY_BACKOFFS_MS[attempt - 1] ?? 2500;
      await new Promise((r) => setTimeout(r, backoff));
    }
    try {
      return await api.get(url, { ...config, timeout: BOOTSTRAP_TIMEOUT_MS });
    } catch (err) {
      lastError = err;
      const willRetry = isRetryableBootstrapError(err) && attempt < 2;
      if (!willRetry) throw err;
    }
  }
  throw lastError || new Error('Bootstrap GET failed');
}

/**
 * POST/PATCH/DELETE with extended timeout + retry for heavy tenant writes (R23).
 * @param {string} url
 * @param {*} [data]
 * @param {import('axios').AxiosRequestConfig} [config]
 */
export async function bootstrapPost(url, data = undefined, config = {}) {
  let lastError = null;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (attempt > 0) {
      const backoff = BOOTSTRAP_RETRY_BACKOFFS_MS[attempt - 1] ?? 2500;
      await new Promise((r) => setTimeout(r, backoff));
    }
    try {
      const timeout = config.timeout ?? BOOTSTRAP_TIMEOUT_MS;
      return await api.post(url, data, { ...config, timeout });
    } catch (err) {
      lastError = err;
      const willRetry = isRetryableBootstrapError(err) && attempt < 2;
      if (!willRetry) throw err;
    }
  }
  throw lastError || new Error('Bootstrap POST failed');
}
